import axios from 'axios';

// The client portal's OWN axios instance and token storage.
//
// Deliberately separate from auth-lib's `apiClient`/`axiosClient`, which carry
// STAFF tokens. Sharing one instance would mean a staff session and a client
// session fighting over the same Authorization header and the same localStorage
// keys — and a firm employee opening the portal on the same browser would send
// their staff token to portal endpoints (or worse, the reverse).
//
// Different storage keys, different instance, different refresh endpoint.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const ACCESS_KEY = 'portalAccessToken';
const REFRESH_KEY = 'portalRefreshToken';

export const getPortalTokens = () => ({
  accessToken: localStorage.getItem(ACCESS_KEY),
  refreshToken: localStorage.getItem(REFRESH_KEY),
});

export const setPortalTokens = (accessToken, refreshToken) => {
  if (accessToken) localStorage.setItem(ACCESS_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
};

export const clearPortalTokens = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

const portalClient = axios.create({ baseURL: API_URL, timeout: 20000 });

portalClient.interceptors.request.use((config) => {
  const { accessToken } = getPortalTokens();
  if (accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Single-flight refresh: several requests failing at once must not each fire
// their own refresh, which would rotate the token repeatedly and invalidate
// the very session they were trying to restore.
let refreshing = null;

portalClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (status !== 401 || original._retry) {
      return Promise.reject(error);
    }
    original._retry = true;

    const { refreshToken } = getPortalTokens();
    if (!refreshToken) {
      clearPortalTokens();
      return Promise.reject(error);
    }

    try {
      if (!refreshing) {
        refreshing = axios
          .post(`${API_URL}/api/portal/auth/refresh`, { refreshToken })
          .finally(() => { refreshing = null; });
      }
      const res = await refreshing;
      setPortalTokens(res.data.accessToken, res.data.refreshToken);
      original.headers.Authorization = `Bearer ${res.data.accessToken}`;
      return portalClient(original);
    } catch (refreshError) {
      clearPortalTokens();
      // Full reload rather than a router navigate: the app's in-memory state
      // belongs to a session that no longer exists.
      if (window.location.pathname.startsWith('/portal')) {
        window.location.href = '/portal/login';
      }
      return Promise.reject(refreshError);
    }
  }
);

export default portalClient;
